// Problem Record — the closed-loop master case file.
// Types are deliberately entity-shaped (problem_record, problem_stage,
// corrective_action, engineering_change, validation_record,
// effectiveness_record) so they can migrate to a database later.

export type ProblemStageState = "Complete" | "Active" | "Pending";

/** Management (governance) status — deliberately separate from technical stage. */
export type ManagementStatus = "Draft" | "Active" | "On Hold" | "Escalated" | "Closed";

export const MANAGEMENT_STATUSES: ManagementStatus[] = [
  "Draft",
  "Active",
  "On Hold",
  "Escalated",
  "Closed",
];

export type ApprovalStatus = "Not Required" | "Pending" | "Approved";

export type ProblemStage = {
  order: number;
  key: string;
  label: string;
  state: ProblemStageState;
  completedOn?: string; // ISO
  due?: string; // ISO
  owner: string;
  summary: string;
  /** Required-evidence field values, keyed by requirement id (see STAGE_REQUIREMENTS). */
  evidence?: Record<string, string>;
  completionNote?: string;
  approvalRequired?: boolean;
  approver?: string;
  approvalStatus?: ApprovalStatus;
};

export type CorrectiveAction = {
  id: string;
  type: "Containment" | "Corrective" | "Preventive";
  action: string;
  owner: string;
  due: string;
  status: "Complete" | "In Progress" | "Planned";
  evidence: string;
};

export type EngineeringChange = {
  id: string;
  title: string;
  changeLevel: "Spec" | "Design" | "Process" | "Supplier";
  status: "Released" | "In Approval" | "Draft";
  releasedOn?: string;
  affectedParts: string;
  ppapLevel: string;
  owner: string;
  detail: string;
};

export type ValidationRecord = {
  id: string;
  test: string;
  method: string;
  sample: string;
  acceptance: string;
  result: string;
  status: "Pass" | "Fail" | "In Test";
  completedOn?: string;
};

export type EffectivenessRecord = {
  metric: string;
  baseline: string;
  current: string;
  target: string;
  status: "On Target" | "Improving" | "Watch";
  window: string;
};

/** Claim snapshot stored on the Problem Record (mirrors WarrantyClaim, DB-ready). */
export type EvidenceClaim = {
  id: string;
  date: string;
  product: string;
  carLine: string;
  component: string;
  supplier: string;
  failureMode: string;
  cost: number;
  mileage: number;
  mis: number;
  severity: "Critical" | "High" | "Medium" | "Low";
  complaint: string;
  repair: string;
  dealer: string;
};

export type ProblemRecord = {
  id: string;
  title: string;
  /** Legacy display string, retained for continuity. Prefer managementStatus + technical stage. */
  status: string;
  /** Governance status — independent of the technical stage. */
  managementStatus?: ManagementStatus;
  holdReason?: string;
  /** Exact claim IDs carried forward from the escalation evidence. */
  linkedClaimIds?: string[];
  /** Immutable snapshot of those claims (prototype resilience; DB would join instead). */
  linkedClaims?: EvidenceClaim[];
  /** Stable key of the escalation source (e.g. "components:C-01"). */
  sourceKey?: string;

  severity: "Critical" | "High" | "Medium" | "Low";
  safetySeverity: string;
  riskScore: number;
  owner: string;
  sponsor: string;
  openedOn: string;
  daysOpen: number;
  source: string;
  products: string[];
  carLines: string[];
  component: string;
  supplier: string;
  failureMode?: string;
  totalClaims: number;
  warrantyCost: number;
  claimsPer1000: number;
  linkedCaseId: string;
  linkedFieldActionId?: string;
  top5ItemId: string;
  narrative: string;
  spcSignal: string;
  predictiveSignal: string;
  /** Prototype provenance — created in-session from a Top 5 item. */
  origin?: "seed" | "created";
  /** Newly created and not yet progressed beyond the warranty signal. */
  isDraft?: boolean;
  /** Trend / recurrence / impact carried forward from Top 5 evidence. */
  trend?: string;
  trendPct?: number;
  repeatOccurrence?: boolean;
  repeatCount?: number;
  customerImpact?: number;
  attention?: string;
  stages: ProblemStage[];
  eightD: { code: string; label: string; state: ProblemStageState; owner: string; note: string }[];
  rootCause: {
    statement: string;
    occurrence: string;
    detection: string;
    systemic: string;
    method: string;
    fiveWhys: string[];
    verification: string;
  };
  correctiveActions: CorrectiveAction[];
  engineeringChanges: EngineeringChange[];
  validation: ValidationRecord[];
  effectiveness: EffectivenessRecord[];
  activity: { ts: string; actor: string; action: string; clause: string }[];
};

export const emptyRootCause = {
  statement: "",
  occurrence: "",
  detection: "",
  systemic: "",
  method: "",
  fiveWhys: [] as string[],
  verification: "",
};

/** Canonical 11-stage closed-loop progression used by every Problem Record. */
export const STAGE_TEMPLATE: { order: number; key: string; label: string }[] = [
  { order: 1, key: "signal", label: "Warranty Signal" },
  { order: 2, key: "definition", label: "Problem Definition" },
  { order: 3, key: "containment", label: "Containment" },
  { order: 4, key: "root-cause", label: "Root Cause" },
  { order: 5, key: "corrective", label: "Corrective Action" },
  { order: 6, key: "eco", label: "Engineering Change" },
  { order: 7, key: "pfmea", label: "PFMEA / Control Plan" },
  { order: 8, key: "validation", label: "Validation" },
  { order: 9, key: "effectiveness", label: "Effectiveness" },
  { order: 10, key: "read-across", label: "Lessons Learned / Read-Across" },
  { order: 11, key: "prevention", label: "Prevention / Closure" },
];

export const problemRecords: ProblemRecord[] = [
  {
    id: "PR-2026-014",
    title: "HV Battery Pack thermal shutdown — PowerCell X7 (cell batch B-2204)",
    status: "Active — Validation",
    severity: "Critical",
    safetySeverity: "S1-Catastrophic (field action tracked separately)",
    riskScore: 92,
    owner: "M. Chen — Quality Engineering",
    sponsor: "D. Shumar — VP Quality",
    openedOn: "2026-04-18",
    daysOpen: 132,
    source: "Top 5 Component · Warranty Signal + SPC 3σ breach",
    products: ["PowerCell X7", "PowerCell X7 LR"],
    carLines: ["EV-X Platform", "EV-X Long Range", "Fleet"],
    component: "HV Battery Pack",
    supplier: "Nordcell Energy AB",
    totalClaims: 412,
    warrantyCost: 3840000,
    claimsPer1000: 33.0,
    linkedCaseId: "8D-2041",
    linkedFieldActionId: "FA-2026-004",
    top5ItemId: "C-01",
    narrative:
      "Field claims for HV battery pack thermal shutdown rose 18.4% MoM and breached the ±3σ control limit for four consecutive months. Claims concentrate on cells from supplier batch B-2204. Containment is in force, root cause is confirmed as separator porosity drift, and corrective actions are through engineering release into validation. Effectiveness monitoring is open until three consecutive months of post-change build data land below target.",
    spcSignal: "Claims SPC (HV Battery) — 4 consecutive points above UCL, CL 21 / UCL 38",
    predictiveSignal:
      "Predictive alert: 87% risk within 30 days · 1,240 units exposed · driver: cell batch #B-2204 anomaly",
    stages: [
      {
        order: 1,
        key: "signal",
        label: "Warranty Signal",
        state: "Complete",
        completedOn: "2026-04-18",
        owner: "Analytics",
        summary: "SPC breach + predictive alert escalated from Top 5 Components.",
      },
      {
        order: 2,
        key: "definition",
        label: "Problem Definition",
        state: "Complete",
        completedOn: "2026-04-21",
        owner: "M. Chen",
        summary: "IS/IS-NOT scoped to B-2204 cells built weeks 12–18.",
      },
      {
        order: 3,
        key: "containment",
        label: "Containment",
        state: "Complete",
        completedOn: "2026-04-25",
        owner: "SQE — K. Okafor",
        summary: "Batch quarantine, 100% IR scan on service stock, BMS derate calibration pushed.",
      },
      {
        order: 4,
        key: "root-cause",
        label: "Root Cause",
        state: "Complete",
        completedOn: "2026-05-30",
        owner: "M. Chen",
        summary: "Separator porosity drift confirmed by teardown and supplier process data.",
      },
      {
        order: 5,
        key: "corrective",
        label: "Corrective Action",
        state: "Complete",
        completedOn: "2026-06-24",
        owner: "M. Chen",
        summary: "Separator spec tightened; supplier SPC gate implemented at cell line.",
      },
      {
        order: 6,
        key: "eco",
        label: "Engineering Change",
        state: "Complete",
        completedOn: "2026-07-09",
        owner: "Design — P. Andersson",
        summary: "ECO-4471 released; PPAP Level 3 resubmission accepted.",
      },
      {
        order: 7,
        key: "pfmea",
        label: "PFMEA / Control Plan",
        state: "Complete",
        completedOn: "2026-07-16",
        owner: "M. Chen",
        summary: "RPN 270 → 36; 100% in-line impedance + IR scan added to control plan.",
      },
      {
        order: 8,
        key: "validation",
        label: "Validation",
        state: "Active",
        owner: "Test — T. Yamada",
        summary:
          "Thermal abuse + 1,000-cycle aging on post-change cells; 2 of 3 protocols complete.",
      },
      {
        order: 9,
        key: "effectiveness",
        label: "Effectiveness",
        state: "Pending",
        owner: "Analytics",
        summary: "Requires 3 consecutive months of post-change field data below 8 C/1000.",
      },
      {
        order: 10,
        key: "read-across",
        label: "Lessons Learned / Read-Across",
        state: "Active",
        owner: "L. García",
        summary:
          "3 sibling programs identified: PowerCell X5 applied, M2 in review, GridStore G1 open.",
      },
      {
        order: 11,
        key: "prevention",
        label: "Prevention / Closure",
        state: "Pending",
        owner: "D. Shumar",
        summary: "Design standard + lessons-learned library update pending validation sign-off.",
      },
    ],
    eightD: [
      {
        code: "D1",
        label: "Team",
        state: "Complete",
        owner: "M. Chen",
        note: "Cross-functional team chartered: QE, SQE, Design, Test, Service.",
      },
      {
        code: "D2",
        label: "Problem Description",
        state: "Complete",
        owner: "M. Chen",
        note: "412 claims, $3.84M exposure, 33.0 C/1000 on EV-X. IS/IS-NOT complete.",
      },
      {
        code: "D3",
        label: "Interim Containment",
        state: "Complete",
        owner: "K. Okafor",
        note: "B-2204 quarantine, service-stock IR scan, BMS derate OTA. Verified effective.",
      },
      {
        code: "D4",
        label: "Root Cause",
        state: "Complete",
        owner: "M. Chen",
        note: "Separator porosity drift on batch B-2204 — confirmed by teardown + supplier SPC data.",
      },
      {
        code: "D5",
        label: "Permanent Corrective Action",
        state: "Complete",
        owner: "M. Chen",
        note: "Tightened separator specification + supplier SPC gate at cell line.",
      },
      {
        code: "D6",
        label: "Implement & Validate",
        state: "Active",
        owner: "T. Yamada",
        note: "ECO-4471 in production; validation protocols 2 of 3 complete.",
      },
      {
        code: "D7",
        label: "Prevent Recurrence",
        state: "Active",
        owner: "L. García",
        note: "PFMEA/Control Plan updated; read-across to 3 sibling programs in flight.",
      },
      {
        code: "D8",
        label: "Team Recognition / Closure",
        state: "Pending",
        owner: "D. Shumar",
        note: "Closure gated on effectiveness review.",
      },
    ],
    rootCause: {
      statement:
        "Cell separator porosity on supplier batch B-2204 drifted above the upper spec bound, reducing internal short margin and allowing localized heating that trips pack thermal protection.",
      occurrence:
        "Supplier calendaring line roll-gap drift went uncontrolled after a preventive-maintenance reset; porosity moved from 41% nominal to 46–48% without an SPC alarm.",
      detection:
        "Incoming inspection sampled electrical capacity only; porosity was not a monitored characteristic, so drifted material passed to build.",
      systemic:
        "PFMEA did not classify separator porosity as a special characteristic, so no control-plan reaction existed at either the supplier or receiving plant.",
      method:
        "5-Why + Ishikawa (Machine / Material branch), verified by teardown metrology on 24 returned packs",
      fiveWhys: [
        "Why did the pack shut down? — Thermal protection tripped on localized cell heating.",
        "Why was there localized heating? — Reduced internal short margin at the separator.",
        "Why was the margin reduced? — Separator porosity exceeded the upper spec bound.",
        "Why did porosity exceed spec? — Calendaring roll-gap drift after a PM reset went uncontrolled.",
        "Why was the drift uncontrolled? — Porosity was not a special characteristic with an SPC gate.",
      ],
      verification:
        "Turn-on / turn-off verified: sourced high-porosity cells reproduced the fault in 6 of 8 abuse tests; spec-compliant cells produced 0 of 8.",
    },
    correctiveActions: [
      {
        id: "CA-001",
        type: "Containment",
        action: "Quarantine all B-2204 cell inventory at plant and service depots",
        owner: "K. Okafor",
        due: "2026-04-25",
        status: "Complete",
        evidence: "Quarantine log QL-2026-118, 8,420 cells held",
      },
      {
        id: "CA-002",
        type: "Containment",
        action: "100% IR + impedance scan on service-stock packs before dispatch",
        owner: "Service Ops",
        due: "2026-04-28",
        status: "Complete",
        evidence: "Scan records, 1,240 packs screened, 31 rejected",
      },
      {
        id: "CA-003",
        type: "Containment",
        action: "BMS derate calibration OTA to exposed VIN population",
        owner: "R. Patel",
        due: "2026-05-02",
        status: "Complete",
        evidence: "OTA campaign 4.6.2, 12,480 VINs, 98.4% uptake",
      },
      {
        id: "CA-004",
        type: "Corrective",
        action: "Tighten separator porosity specification to 41% ±2% with Cpk ≥ 1.67",
        owner: "M. Chen",
        due: "2026-06-20",
        status: "Complete",
        evidence: "Spec SPC-SEP-0442 rev C, supplier acknowledgement signed",
      },
      {
        id: "CA-005",
        type: "Corrective",
        action: "Install supplier SPC gate on calendaring roll-gap with automatic line hold",
        owner: "SQE — K. Okafor",
        due: "2026-06-24",
        status: "Complete",
        evidence: "Supplier audit report SA-2026-77, control charts live",
      },
      {
        id: "CA-006",
        type: "Preventive",
        action: "Add 100% in-line impedance + IR scan to cell formation control plan",
        owner: "M. Chen",
        due: "2026-07-16",
        status: "Complete",
        evidence: "Control Plan CP-PCX7 rev 8",
      },
      {
        id: "CA-007",
        type: "Preventive",
        action: "Classify separator porosity as a special characteristic in the design standard",
        owner: "P. Andersson",
        due: "2026-09-15",
        status: "In Progress",
        evidence: "DS-BAT-014 draft in review",
      },
      {
        id: "CA-008",
        type: "Preventive",
        action: "Read-across porosity gate to all B-22xx-supplied cell programs",
        owner: "L. García",
        due: "2026-09-30",
        status: "In Progress",
        evidence: "3 target programs tracked in Read-Across register",
      },
    ],
    engineeringChanges: [
      {
        id: "ECO-4471",
        title: "Separator porosity specification tightened to 41% ±2%",
        changeLevel: "Spec",
        status: "Released",
        releasedOn: "2026-07-09",
        affectedParts: "CELL-B22-XX, PACK-PCX7-A/B",
        ppapLevel: "Level 3 resubmission",
        owner: "P. Andersson",
        detail:
          "Supersedes SPC-SEP-0442 rev B. Adds Cpk ≥ 1.67 requirement and supplier SPC evidence as a PPAP deliverable.",
      },
      {
        id: "ECO-4488",
        title: "In-line impedance + IR scan station at cell formation",
        changeLevel: "Process",
        status: "Released",
        releasedOn: "2026-07-14",
        affectedParts: "Line 3 formation & aging",
        ppapLevel: "Process change notice",
        owner: "Mfg Eng — S. Müller",
        detail:
          "Adds 100% screening with automatic reject-to-MRB and SQE notification on out-of-band impedance.",
      },
      {
        id: "ECO-4502",
        title: "Pack-level thermal barrier interleave (design hardening)",
        changeLevel: "Design",
        status: "In Approval",
        affectedParts: "PACK-PCX7-C",
        ppapLevel: "Level 3 (planned)",
        owner: "P. Andersson",
        detail:
          "Defence-in-depth change to limit propagation independent of cell quality. Targeted for MY27 build.",
      },
    ],
    validation: [
      {
        id: "VAL-01",
        test: "Thermal abuse — nail penetration",
        method: "UN 38.3 / internal TP-BAT-221",
        sample: "n = 12 post-change cells",
        acceptance: "No thermal propagation beyond initiating cell",
        result: "12 / 12 contained",
        status: "Pass",
        completedOn: "2026-08-02",
      },
      {
        id: "VAL-02",
        test: "1,000-cycle accelerated aging",
        method: "TP-BAT-118, 45 °C, 1C/1C",
        sample: "n = 24 cells, 3 lots",
        acceptance: "Impedance rise ≤ 12%, porosity stable",
        result: "Mean rise 8.4%, no outliers",
        status: "Pass",
        completedOn: "2026-08-15",
      },
      {
        id: "VAL-03",
        test: "Pack-level field-correlation soak",
        method: "TP-PACK-090 duty-cycle replay",
        sample: "n = 6 packs",
        acceptance: "Zero isolation or derate faults over 90-day replay",
        result: "Day 51 of 90 — zero faults to date",
        status: "In Test",
      },
      {
        id: "VAL-04",
        test: "Supplier process capability audit",
        method: "IATF 16949 §8.4.2.4 layered audit",
        sample: "12 weeks of calendaring SPC data",
        acceptance: "Cpk ≥ 1.67 on porosity",
        result: "Cpk 1.83 over 12 weeks",
        status: "Pass",
        completedOn: "2026-07-31",
      },
    ],
    effectiveness: [
      {
        metric: "Claims / 1,000 (post-change build)",
        baseline: "33.0",
        current: "6.8",
        target: "≤ 8.0",
        status: "On Target",
        window: "3 months post-ECO",
      },
      {
        metric: "Monthly claims (HV battery)",
        baseline: "48 / mo",
        current: "11 / mo",
        target: "≤ 14 / mo",
        status: "On Target",
        window: "Rolling 90 days",
      },
      {
        metric: "SPC state",
        baseline: "4 pts > UCL",
        current: "In control (CL 21, UCL 38)",
        target: "In control 3 consecutive months",
        status: "Improving",
        window: "2 of 3 months achieved",
      },
      {
        metric: "Warranty cost run-rate",
        baseline: "$640K / mo",
        current: "$148K / mo",
        target: "≤ $180K / mo",
        status: "On Target",
        window: "Rolling 90 days",
      },
      {
        metric: "Field safety incidents",
        baseline: "3 reports",
        current: "0 since 2026-06-30",
        target: "0",
        status: "Watch",
        window: "Monitored until closure",
      },
      {
        metric: "PFMEA RPN",
        baseline: "270",
        current: "36",
        target: "≤ 60",
        status: "On Target",
        window: "Post-corrective",
      },
    ],
    activity: [
      {
        ts: "2026-08-26 09:12",
        actor: "T. Yamada",
        action: "VAL-03 interim result logged — day 51, zero faults",
        clause: "IATF 16949 §8.6.2",
      },
      {
        ts: "2026-08-15 14:40",
        actor: "T. Yamada",
        action: "VAL-02 accelerated aging closed as Pass",
        clause: "ISO 9001:2015 §8.3.4",
      },
      {
        ts: "2026-08-04 11:05",
        actor: "L. García",
        action: "Read-across target PowerCell X5 marked Applied",
        clause: "IATF 16949 §10.2.3",
      },
      {
        ts: "2026-07-16 16:22",
        actor: "M. Chen",
        action: "Control Plan CP-PCX7 rev 8 released — RPN 270 → 36",
        clause: "IATF 16949 §8.5.1.1",
      },
      {
        ts: "2026-07-09 10:31",
        actor: "P. Andersson",
        action: "ECO-4471 released, PPAP Level 3 accepted",
        clause: "IATF 16949 §8.3.6.1",
      },
      {
        ts: "2026-06-24 08:58",
        actor: "K. Okafor",
        action: "Supplier SPC gate verified on-site at Nordcell",
        clause: "IATF 16949 §8.4.2.4",
      },
      {
        ts: "2026-05-30 13:47",
        actor: "M. Chen",
        action: "D4 root cause confirmed and verified (turn-on/turn-off)",
        clause: "IATF 16949 §10.2.3",
      },
      {
        ts: "2026-04-25 07:20",
        actor: "K. Okafor",
        action: "Containment verified effective — batch quarantine complete",
        clause: "ISO 9001:2015 §10.2.1",
      },
      {
        ts: "2026-04-18 06:02",
        actor: "System",
        action: "Problem Record opened from Top 5 Components (SPC 3σ breach)",
        clause: "IATF 16949 §9.1.1.1",
      },
    ],
  },
];

export function getProblemRecord(id: string): ProblemRecord | undefined {
  return problemRecords.find((p) => p.id.toLowerCase() === id.toLowerCase());
}
